import {
  COMPOUNDS,
  LY_ADVERBS,
  MONTHS,
  NAME_PAIRS,
  NOT_A_NOUN,
  NUMBER_WORDS,
  UNIT_WORDS,
  WEEKDAYS,
} from "@/lib/tool-kit/style-checkers/hyphen-dash-data";
import {
  insideAny,
  quotedSpans,
  type Span,
  withoutOverlaps,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Hyphens, en dashes and em dashes, checked against the conventions APA 7 and Chicago 18
 * share:
 *
 *   hyphen     joins the parts of a compound: a well-known author, a 5-year-old child
 *   en dash    joins a range (pp. 45–67, Monday–Friday) and, in Chicago, names or an open
 *              compound used as one modifier (Mann–Whitney test, pre–Civil War)
 *   em dash    sets off an aside or an abrupt break, closed up — like this
 *
 * Every rule is a pattern with a fix attached; none parses grammar. The compound rules
 * work from curated lists, so they are precise about the compounds they know and silent
 * about the rest. Quoted passages and URLs, DOIs, e-mail addresses and ISSN/ISBN numbers
 * are skipped.
 */

export type Style = "apa" | "chicago";

export type RuleId =
  | "range-hyphen"
  | "range-em"
  | "range-spaced"
  | "range-words"
  | "range-from"
  | "dash-double"
  | "dash-spaced-hyphen"
  | "dash-spaced-em"
  | "dash-spaced-en"
  | "en-between-words"
  | "compound-open"
  | "compound-after"
  | "ly-hyphen"
  | "number-unit"
  | "name-pair"
  | "line-break";

/** "fix" is mechanical, "check" needs a look at the sentence. */
export type Severity = "fix" | "check";

export interface Finding extends Span {
  rule: RuleId;
  severity: Severity;
  text: string;
  /** What the text should read instead; null when the checker cannot say. */
  replacement: string | null;
}

export interface Options {
  style: Style;
  /** British practice sets an aside off with a spaced en dash; then " – " is not reported. */
  allowSpacedEn: boolean;
  /** Leave quoted passages alone. */
  skipQuotes: boolean;
}

export const DEFAULT_OPTIONS: Options = {
  style: "apa",
  allowSpacedEn: false,
  skipQuotes: true,
};

const EN = "–";
const EM = "—";

/* ------------------------------------------------------------ patterns */

const NUM = String.raw`(?:\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)`;
const BEFORE_NUM = String.raw`(?<![\p{L}\p{N}.,:-])`;
const AFTER_NUM = String.raw`(?![\p{L}\p{N}:-]|[.,]\d)`;

const RE_RANGE_HYPHEN = new RegExp(
  `${BEFORE_NUM}(${NUM})-(${NUM})${AFTER_NUM}`,
  "gu"
);
const RE_RANGE_EM = new RegExp(
  `${BEFORE_NUM}(${NUM})${EM}(${NUM})${AFTER_NUM}`,
  "gu"
);
const RE_RANGE_SPACED = new RegExp(
  `${BEFORE_NUM}(${NUM})[ \\t]+[${EN}-][ \\t]+(${NUM})${AFTER_NUM}`,
  "gu"
);
const RE_FROM_RANGE = new RegExp(
  `\\b(from|between)[ \\t]+(${NUM})[ \\t]*[${EN}-][ \\t]*(${NUM})${AFTER_NUM}`,
  "giu"
);

function dayNames(): string {
  return WEEKDAYS.join("|");
}

const RE_DAY_RANGE = new RegExp(
  `\\b(${dayNames()})[${EN}-](${dayNames()})\\b`,
  "gu"
);
const RE_MONTH_RANGE = new RegExp(
  `\\b(${MONTHS.join("|")})[${EN}-](${MONTHS.join("|")})\\b`,
  "gu"
);

const AROUND = String.raw`[\p{L}\p{N}.,;:!?)”"'’]`;
const BEFORE_OPEN = String.raw`[\p{L}\p{N}(“"'‘]`;
const RE_DOUBLE_HYPHEN = new RegExp(
  `(?<=${AROUND})[ \\t]*--+[ \\t]*(?=${BEFORE_OPEN})`,
  "gu"
);
const RE_SPACED_HYPHEN = new RegExp(
  `(?<=${AROUND})[ \\t]+-[ \\t]+(?=${BEFORE_OPEN})`,
  "gu"
);
const RE_SPACED_EM = new RegExp(
  `(?<=\\S)[ \\t]+${EM}[ \\t]*(?=\\S)|(?<=\\S)[ \\t]*${EM}[ \\t]+(?=\\S)`,
  "gu"
);
const RE_SPACED_EN = new RegExp(
  `(?<=[\\p{L}.,;:!?)”’])[ \\t]+${EN}[ \\t]+(?=[\\p{L}(“‘])`,
  "gu"
);
const RE_EN_BETWEEN_WORDS = new RegExp(
  `(?<=\\p{Ll}{2})${EN}(?=\\p{Ll}{2})`,
  "gu"
);
const RE_LINE_BREAK = /(?<=\p{L}{2})-[ \t]*\r?\n[ \t]*(?=\p{Ll}{2})/gu;

const RE_LY_HYPHEN = new RegExp(
  `(?<![\\p{L}-])(?:${LY_ADVERBS.join("|")})-(?=\\p{L})`,
  "giu"
);

const NAME_ALTERNATION = NAME_PAIRS.join("|");
const RE_NAME_PAIR = new RegExp(`\\b(?:${NAME_ALTERNATION})\\b`, "gu");

const COMPOUND_ALTERNATION = COMPOUNDS.map((c) =>
  c.open.split(" ").join("[ \\t]+")
).join("|");
const RE_COMPOUND_OPEN = new RegExp(
  `(?<![\\p{L}-])(${COMPOUND_ALTERNATION})(?![\\p{L}-])(?=[ \\t]+(\\p{L}[\\p{L}'’]*))`,
  "giu"
);
const HYPHENATED_ALTERNATION = COMPOUNDS.map((c) => c.hyphenated).join("|");
const RE_COMPOUND_AFTER = new RegExp(
  `\\b(?:is|are|was|were|be|been|being|seems?|seemed|becomes?|became|remains?|remained|appears?|appeared)[ \\t]+(?:(?:very|quite|rather|fairly|also|still|not|too|so|more|most|less|least)[ \\t]+)?(${HYPHENATED_ALTERNATION})(?=[ \\t]*[.,;:!?)]|[ \\t]+(?:and|or|but|for|to|in|on|at|of|by|with|from|as|than|that|which|when)\\b|[ \\t]*$)`,
  "gimu"
);
const UNITS = UNIT_WORDS.join("|");
const NUMBERS = `\\d+|${NUMBER_WORDS.join("|")}`;
const RE_NUMBER_UNIT = new RegExp(
  `\\b(?:a|an|the|this|that|each|every|our|their|its|his|her|my|your)[ \\t]+((?:${NUMBERS})[ \\t]+(?:${UNITS})(?:[ \\t]+old)?)(?=[ \\t]+(\\p{L}+))`,
  "giu"
);

const RE_SKIP =
  /(?:https?:\/\/|www\.)\S+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|\b10\.\d{4,9}\/\S+|\b(?:ISSN|ISBN|PMID|PMCID|arXiv)[:\s]*[\w./-]+/giu;
const RE_ARITHMETIC_AFTER = /^[ \t]*[=+×*/÷]/;
const RE_ARITHMETIC_BEFORE = /[=+×*/÷][ \t]*$/;
const RE_UPPER = /^\p{Lu}/u;
const RE_SPACES = /[ \t]+/g;

/* ------------------------------------------------------------ helpers */

function make(
  rule: RuleId,
  severity: Severity,
  start: number,
  text: string,
  replacement: string | null
): Finding {
  return { rule, severity, start, end: start + text.length, text, replacement };
}

function matchCase(original: string, replacement: string): string {
  if (original.length > 1 && original === original.toUpperCase()) {
    return replacement.toUpperCase();
  }
  return RE_UPPER.test(original)
    ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
    : replacement;
}

function isArithmetic(text: string, start: number, end: number): boolean {
  const lineStart = text.lastIndexOf("\n", start - 1) + 1;
  const lineEndIndex = text.indexOf("\n", end);
  const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
  return (
    RE_ARITHMETIC_AFTER.test(text.slice(end, lineEnd)) ||
    RE_ARITHMETIC_BEFORE.test(text.slice(lineStart, start))
  );
}

const RE_MONTH_NUMBER = /^(?:0[1-9]|1[0-2])$/;

/** "555-1234" and "2020-10" look like ranges and are not. */
function isNotARange(first: string, second: string): boolean {
  const phone = first.length === 3 && second.length === 4;
  const yearMonth = first.length === 4 && RE_MONTH_NUMBER.test(second);
  return phone || yearMonth;
}

/* ------------------------------------------------------------ rules */

type Rule = (text: string) => Finding[];

function rangeHyphen(text: string): Finding[] {
  const found: Finding[] = [];
  for (const m of text.matchAll(RE_RANGE_HYPHEN)) {
    const end = m.index + m[0].length;
    if (isNotARange(m[1], m[2]) || isArithmetic(text, m.index, end)) {
      continue;
    }
    found.push(
      make("range-hyphen", "fix", m.index, m[0], `${m[1]}${EN}${m[2]}`)
    );
  }
  return found;
}

function rangeEm(text: string): Finding[] {
  return [...text.matchAll(RE_RANGE_EM)].map((m) =>
    make("range-em", "fix", m.index, m[0], `${m[1]}${EN}${m[2]}`)
  );
}

function rangeSpaced(text: string): Finding[] {
  const found: Finding[] = [];
  for (const m of text.matchAll(RE_RANGE_SPACED)) {
    if (!isArithmetic(text, m.index, m.index + m[0].length)) {
      found.push(
        make("range-spaced", "fix", m.index, m[0], `${m[1]}${EN}${m[2]}`)
      );
    }
  }
  return found;
}

function rangeFrom(text: string): Finding[] {
  return [...text.matchAll(RE_FROM_RANGE)].map((m) => {
    const lead = m[1];
    const joiner = lead.toLowerCase() === "from" ? "to" : "and";
    return make(
      "range-from",
      "fix",
      m.index,
      m[0],
      `${lead} ${m[2]} ${joiner} ${m[3]}`
    );
  });
}

function rangeWords(text: string): Finding[] {
  return [...text.matchAll(RE_DAY_RANGE), ...text.matchAll(RE_MONTH_RANGE)].map(
    (m) => make("range-words", "fix", m.index, m[0], `${m[1]}${EN}${m[2]}`)
  );
}

function doubleHyphen(text: string): Finding[] {
  return [...text.matchAll(RE_DOUBLE_HYPHEN)].map((m) =>
    make("dash-double", "fix", m.index, m[0], EM)
  );
}

const RE_DIGIT = /\d/;

function spacedHyphen(text: string): Finding[] {
  const found: Finding[] = [];
  for (const m of text.matchAll(RE_SPACED_HYPHEN)) {
    const end = m.index + m[0].length;
    const numeric =
      RE_DIGIT.test(text[m.index - 1] ?? "") && RE_DIGIT.test(text[end] ?? "");
    // A spaced hyphen between numbers is a range (range-spaced) or a minus sign.
    if (!(numeric || isArithmetic(text, m.index, end))) {
      found.push(make("dash-spaced-hyphen", "fix", m.index, m[0], EM));
    }
  }
  return found;
}

function spacedEm(text: string): Finding[] {
  return [...text.matchAll(RE_SPACED_EM)].map((m) =>
    make("dash-spaced-em", "fix", m.index, m[0], EM)
  );
}

function spacedEn(text: string): Finding[] {
  return [...text.matchAll(RE_SPACED_EN)].map((m) =>
    make("dash-spaced-en", "check", m.index, m[0], EM)
  );
}

function enBetweenWords(text: string): Finding[] {
  return [...text.matchAll(RE_EN_BETWEEN_WORDS)].map((m) =>
    make("en-between-words", "check", m.index, m[0], "-")
  );
}

function lineBreak(text: string): Finding[] {
  return [...text.matchAll(RE_LINE_BREAK)].map((m) =>
    make("line-break", "check", m.index, m[0], "")
  );
}

function lyHyphen(text: string): Finding[] {
  return [...text.matchAll(RE_LY_HYPHEN)].map((m) =>
    make("ly-hyphen", "fix", m.index, m[0], `${m[0].slice(0, -1)} `)
  );
}

function namePair(text: string): Finding[] {
  return [...text.matchAll(RE_NAME_PAIR)].map((m) =>
    make("name-pair", "fix", m.index, m[0], m[0].replace("-", EN))
  );
}

const HYPHENATED_BY_OPEN = new Map(
  COMPOUNDS.map((c) => [c.open, c.hyphenated])
);
const OPEN_BY_HYPHENATED = new Map(
  COMPOUNDS.map((c) => [c.hyphenated, c.open])
);

function compoundOpen(text: string): Finding[] {
  const found: Finding[] = [];
  for (const m of text.matchAll(RE_COMPOUND_OPEN)) {
    const next = m[2].toLowerCase();
    if (NOT_A_NOUN.has(next)) {
      continue;
    }
    const open = m[1].toLowerCase().replace(RE_SPACES, " ");
    const hyphenated = HYPHENATED_BY_OPEN.get(open);
    if (hyphenated) {
      found.push(
        make("compound-open", "fix", m.index, m[1], matchCase(m[1], hyphenated))
      );
    }
  }
  return found;
}

function compoundAfter(text: string): Finding[] {
  const found: Finding[] = [];
  for (const m of text.matchAll(RE_COMPOUND_AFTER)) {
    const open = OPEN_BY_HYPHENATED.get(m[1].toLowerCase());
    if (open) {
      const start = m.index + m[0].lastIndexOf(m[1]);
      found.push(
        make("compound-after", "fix", start, m[1], matchCase(m[1], open))
      );
    }
  }
  return found;
}

function numberUnit(text: string): Finding[] {
  const found: Finding[] = [];
  for (const m of text.matchAll(RE_NUMBER_UNIT)) {
    if (NOT_A_NOUN.has(m[2].toLowerCase())) {
      continue;
    }
    const start = m.index + m[0].lastIndexOf(m[1]);
    found.push(
      make("number-unit", "fix", start, m[1], m[1].replace(RE_SPACES, "-"))
    );
  }
  return found;
}

/* ------------------------------------------------------------ scan */

export interface Report {
  findings: Finding[];
  counts: Record<RuleId, number>;
  fixable: number;
}

function emptyCounts(): Record<RuleId, number> {
  return {
    "range-hyphen": 0,
    "range-em": 0,
    "range-spaced": 0,
    "range-words": 0,
    "range-from": 0,
    "dash-double": 0,
    "dash-spaced-hyphen": 0,
    "dash-spaced-em": 0,
    "dash-spaced-en": 0,
    "en-between-words": 0,
    "compound-open": 0,
    "compound-after": 0,
    "ly-hyphen": 0,
    "number-unit": 0,
    "name-pair": 0,
    "line-break": 0,
  };
}

const COMMON_RULES: readonly Rule[] = [
  rangeFrom,
  rangeHyphen,
  rangeEm,
  rangeSpaced,
  rangeWords,
  doubleHyphen,
  spacedHyphen,
  spacedEm,
  enBetweenWords,
  lineBreak,
  lyHyphen,
  compoundOpen,
  compoundAfter,
  numberUnit,
];

function skipSpans(text: string): Span[] {
  return [...text.matchAll(RE_SKIP)].map((m) => ({
    start: m.index,
    end: m.index + m[0].length,
  }));
}

/** Check `text`. Offsets index the reader's own text; findings never overlap. */
export function checkHyphensAndDashes(
  text: string,
  options: Options = DEFAULT_OPTIONS
): Report {
  const rules: Rule[] = [...COMMON_RULES];
  if (!options.allowSpacedEn) {
    rules.push(spacedEn);
  }
  if (options.style === "chicago") {
    rules.push(namePair);
  }
  const quoted = options.skipQuotes ? quotedSpans(text) : [];
  const skips = skipSpans(text);
  const found = rules
    .flatMap((rule) => rule(text))
    .filter(
      (finding) =>
        !(
          insideAny(quoted, finding.start, finding.end) ||
          insideAny(skips, finding.start, finding.end) ||
          finding.text === finding.replacement
        )
    );
  const findings = withoutOverlaps(found);
  const counts = emptyCounts();
  for (const finding of findings) {
    counts[finding.rule] += 1;
  }
  return {
    findings,
    counts,
    fixable: findings.filter((f) => f.severity === "fix").length,
  };
}

/** Apply `replacement` to every finding in `chosen` (end to start, so offsets hold). */
export function applyFindings(
  text: string,
  chosen: readonly Finding[]
): string {
  let result = text;
  for (const finding of [...chosen].sort((a, b) => b.start - a.start)) {
    if (finding.replacement !== null) {
      result =
        result.slice(0, finding.start) +
        finding.replacement +
        result.slice(finding.end);
    }
  }
  return result;
}
